import React, { useEffect, useState, useRef } from 'react';
import {
  Table,
  Input,
  Select,
  Button,
  Image,
  Space,
  Form,
  Modal,
  message,
} from 'antd';
import type { ColumnsType, TablePaginationConfig } from 'antd/es/table';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  getArtworkStylePage,
  getAllArtworkStyleTypes,
  deleteArtworkStyle,
  ArtworkAiLiblibStyleDictDTO,
  GetArtworkStylePageParams,
  artworkLiblibTypeStyle,
} from '@/api/liblib';
import dayjs from 'dayjs';
import { PageLink } from '@/config/pageConfig';

const { Option } = Select;

const LiblibList: React.FC = () => {
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [dataSource, setDataSource] = useState<ArtworkAiLiblibStyleDictDTO[]>(
    []
  );
  const [loading, setLoading] = useState(false);
  const [styleTypeOptions, setStyleTypeOptions] = useState<string[]>([]);
  // 用于标记是否是内部操作导致的 URL 变化
  const isInternalChangeRef = useRef(false);
  
  // 从 URL 参数中读取页码，默认为 1
  const initialPage = parseInt(searchParams.get('page') || '1', 10);
  const [pagination, setPagination] = useState<TablePaginationConfig>({
    current: initialPage,
    pageSize: 10,
    total: 0,
    showSizeChanger: false,
  });

  useEffect(() => {
    fetchStyleTypes();
    // 初始化时从 URL 读取页码并获取数据
    const pageFromUrl = parseInt(searchParams.get('page') || '1', 10);
    fetchData({ pageNum: pageFromUrl, pageSize: 10 });
  }, []);

  // 监听 URL 参数变化，当页码变化时重新获取数据（仅处理外部变化，如从详情页返回）
  useEffect(() => {
    // 如果是内部操作导致的 URL 变化，跳过处理
    if (isInternalChangeRef.current) {
      isInternalChangeRef.current = false;
      return;
    }
    
    const pageFromUrl = parseInt(searchParams.get('page') || '1', 10);
    // 只有当 URL 中的页码与当前分页状态不一致时才更新
    if (pageFromUrl !== pagination.current) {
      setPagination((prev) => ({
        ...prev,
        current: pageFromUrl,
      }));
      fetchData({ pageNum: pageFromUrl, pageSize: pagination.pageSize || 10 });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const fetchStyleTypes = async () => {
    const { data } = await artworkLiblibTypeStyle();
    setStyleTypeOptions(
      data.data.filter((item) => item.styleTypeId).filter(Boolean)
    );
  };

  const fetchData = async (pageParams?: GetArtworkStylePageParams) => {
    setLoading(true);
    const values = form.getFieldsValue();
    const query = {
      ...values,
      styleId: values.styleId ? values.styleId : null,
      styleType: values.styleType ? values.styleType : null,
      styleName: values.styleName ? values.styleName : null,
    };
    const { current = 1, pageSize = 10 } = pagination;
    // 优先使用传入的参数
    const targetPage = pageParams?.pageNum ?? current;
    const targetPageSize = pageParams?.pageSize ?? pageSize;
    
    try {
      const { data } = await getArtworkStylePage(query, {
        pageNum: targetPage,
        pageSize: targetPageSize,
      });

      const { records, total, size, current: page } = data.data;

      setDataSource(records || []);
      setPagination((prev) => ({
        ...prev,
        total,
        // 使用后端返回的页码，确保与后端一致
        current: page,
        pageSize: size,
      }));
    } finally {
      setLoading(false);
    }
  };

  const onSearch = () => {
    // 标记这是内部操作
    isInternalChangeRef.current = true;
    // 重置页码
    setPagination((prev) => ({ ...prev, current: 1 }));
    // 更新 URL 参数
    const params = new URLSearchParams(searchParams);
    params.set('page', '1');
    setSearchParams(params, { replace: true });
    // 获取数据
    fetchData({ pageNum: 1, pageSize: pagination.pageSize || 10 });
  };

  const onEdit = (record: ArtworkAiLiblibStyleDictDTO) => {
    // 跳转到详情页时携带当前页码
    const currentPage = pagination.current || 1;
    navigate(`${PageLink.Liblib}/detail/${record.styleId}?page=${currentPage}`);
  };

  const onDelete = (styleId?: number) => {
    if (!styleId) return;
    Modal.confirm({
      title: '确定删除该风格？',
      content: '删除后将无法恢复',
      okText: '确认',
      cancelText: '取消',
      onOk: async () => {
        try {
          const res = await deleteArtworkStyle(styleId);
          if (res.data.success) {
            message.success('删除成功');
            fetchData();
          } else {
            message.error(res.data.message || '删除失败');
          }
        } catch {
          message.error('删除请求失败');
        }
      },
    });
  };

  const handleTableChange = (newPagination: TablePaginationConfig) => {
    const targetPage = newPagination.current || 1;
    const targetPageSize = newPagination.pageSize || pagination.pageSize || 10;
    
    // 标记这是内部操作
    isInternalChangeRef.current = true;
    
    // 先更新分页状态
    setPagination((prev) => ({
      ...prev,
      current: targetPage,
      pageSize: targetPageSize,
    }));
    
    // 更新 URL 参数
    const params = new URLSearchParams(searchParams);
    params.set('page', String(targetPage));
    setSearchParams(params, { replace: true });
    
    // 获取数据
    fetchData({
      pageNum: targetPage,
      pageSize: targetPageSize,
    });
  };

  const columns: ColumnsType<ArtworkAiLiblibStyleDictDTO> = [
    { title: '风格ID', dataIndex: 'styleId', key: 'styleId', width: 80 },
    {
      title: '画风预览',
      dataIndex: 'styleImage',
      key: 'styleImage',
      width: 100,
      render: (url) => (url ? <Image width={50} src={url} alt="预览" /> : '-'),
    },
    {
      title: '风格类型',
      dataIndex: 'styleTypeId',
      key: 'styleType',
      render: (v: number, record) => {
        const styleType = styleTypeOptions.find(item => item.styleTypeId === v);
        return styleType?.styleType ?? "-"
      }
    },
    // {
    //   title: '风格类型排序',
    //   dataIndex: 'styleTypeSort',
    //   key: 'styleTypeSort',
    //   width: 120,
    // },
    { title: '主题名称', dataIndex: 'styleName', key: 'styleName' },
    {
      title: '主题排序',
      dataIndex: 'styleNameSort',
      key: 'styleNameSort',
      width: 100,
    },
    {
      title: '是否推荐',
      dataIndex: 'isRecommend',
      key: 'isRecommend',
      width: 100,
      render: (v) => (v ? '是' : '否'),
    },
    {
      title: '推荐排序',
      dataIndex: 'recommendSort',
      key: 'recommendSort',
      width: 100,
      render: (v) => v ?? '-',
    },
    {
      title: '最低等级',
      dataIndex: 'minTierLevel',
      key: 'minTierLevel',
      width: 100,
      render: (v: number) => {
        const map: Record<number, string> = { [-1]: 'Free', 2: 'Lite', 0: 'Pro', 1: 'Premium' };
        return map[v] ?? 'Free';
      },
    },
    {
      title: '操作时间',
      dataIndex: 'updateTime',
      key: 'updateTime',
      width: 160,
      render: (val) => (val ? dayjs(val).format('YYYY-MM-DD HH:mm') : '-'),
    },
    {
      title: '操作',
      key: 'action',
      fixed: 'right',
      width: 120,
      render: (_, record) => (
        <Space>
          <a onClick={() => onEdit(record)}>编辑</a>
          <a onClick={() => onDelete(record.styleId)}>删除</a>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <Form layout="inline" form={form}>
        <Form.Item name="styleId" label="风格ID">
          <Input placeholder="请输入风格ID" style={{ width: 140 }} allowClear />
        </Form.Item>
        <Form.Item name="styleTypeId" label="风格类型">
          <Select
            allowClear
            style={{ width: 180 }}
            placeholder="请选择风格类型"
          >
            {styleTypeOptions.map((type) => (
              <Option value={type.styleTypeId} key={type.styleTypeId}>
                {type.styleType}
              </Option>
            ))}
          </Select>
        </Form.Item>
        <Form.Item name="styleName" label="主题名称">
          <Input
            placeholder="请输入主题名称"
            style={{ width: 180 }}
            allowClear
          />
        </Form.Item>
        <Form.Item name="isRecommend" label="是否推荐">
          <Select
            allowClear
            style={{ width: 140 }}
            placeholder="请选择是否推荐"
          >
            <Option value={1}>是</Option>
            <Option value={0}>否</Option>
          </Select>
        </Form.Item>
        <Form.Item>
          <Button type="primary" onClick={onSearch}>
            搜索
          </Button>
        </Form.Item>
        <Form.Item>
          <Button
            type="default"
            onClick={() => navigate(`${PageLink.Liblib}/create`)}
          >
            创建
          </Button>
        </Form.Item>
      </Form>

      <Table
        rowKey="styleId"
        columns={columns}
        dataSource={dataSource}
        loading={loading}
        pagination={pagination}
        onChange={handleTableChange}
        style={{ marginTop: 20 }}
        scroll={{ x: 'max-content' }}
      />
    </div>
  );
};

export default LiblibList;
